/**
 * `<SnapshotsView/>` — named snapshots (issue #765, B3.4): the curated
 * "moments worth returning to," by name.
 *
 * The surface, faithful to `B3-4-named-snapshots.svg`:
 *   - a header (`[team] · Snapshots`) with a teal "+ Pin current state" action;
 *   - the list of snapshots — each a row with a clock glyph, its label, a
 *     "<date> · pinned by <who>" sub-line, and an "open →" affordance;
 *   - a footer note: "Snapshots are the moments worth returning to, by name.
 *     Pinning never edits the plan."
 *
 * Two interactions:
 *   - **Pin** opens a small inline form (label + optional date; default "now")
 *     → `useCreateTaskSnapshot`, which invalidates the list so the new row
 *     appears. The form is calm + dismissable; no modal chrome.
 *   - **Open** a row → renders the as-of view at the snapshot's instant
 *     (`<SnapshotAsOfView/>`, which reuses the #764 read-only as-of canvas).
 *
 * State: this view owns only "which snapshot is open" + "is the pin form
 * showing"; the data lives in the hooks. The open state is internal so the
 * gated entry point (the MapEditor) only knows "snapshots surface open/closed."
 *
 * Graceful degradation: a not-yet-deployed endpoint (`isError`) → a calm
 * unavailable state; an empty list → a calm "no snapshots yet" prompt. Never
 * a crash.
 *
 * Brand firewall (§A9): a snapshot bookmarks the PLAN's state. `created_by`
 * shows light provenance ("pinned by AT") — never a tally, never a count
 * about a person. Pinning is read-only on the plan ("never edits the plan").
 *
 * Editorial revamp (#1043, Wave 2): theme-aware via `useMapTheme()`. Default
 * LEGACY for every team until the revamp lands, so:
 *   - LEGACY (flag off): the shipped look — `⟲ Snapshots` header, teal pin
 *     button, soft rows with a clock glyph. Byte-identical to today.
 *   - EDITORIAL (flag on): the wireframe `V_B34` — a mono `SNAPSHOTS` kicker +
 *     a serif-italic "Moments worth returning to." heading, a `.t-btn` "+ Pin
 *     current state", a `.t-card` list of pinned moments (glyph + serif-italic
 *     name + mono date + "pinned by <Avatar> <name>" + a `.t-btn.link`
 *     "open →"), and the italic "Pinning never edits the plan." footer. The
 *     "pinned by" avatar uses the theme-aware `<AvatarRow/>`. RE-SKIN, not
 *     rebuild: every `data-testid`, copy, and the pin/open behaviour unchanged.
 *
 * Isolation: taskmap-local imports only — no scheduler coupling.
 */

import { useEffect, useRef, useState } from "react";

import {
  type TaskSnapshot,
  useCreateTaskSnapshot,
  useTaskSnapshots,
} from "../api/useTaskSnapshots";
import { AvatarRow } from "../components/Avatar";
import { type TaskMember, deriveInitials } from "../members/useTaskTeamMembers";
import { formatDayLabel } from "../rewind/rewindTimeline";
import { useMapTheme } from "../theme/MapThemeContext";
import { PALETTE } from "../tree/palette";
import { SnapshotAsOfView } from "./SnapshotAsOfView";

/** "May 12" — the row's pinned-instant label. */
function rowDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : formatDayLabel(d);
}

/**
 * Light provenance for the row sub-line. `created_by` is whatever the server
 * recorded (a short actor reference); when absent we read "pinned" with no
 * actor — never a fabricated name, never a tally (§A9).
 */
function pinnedByLabel(snapshot: TaskSnapshot): string {
  const who = snapshot.created_by?.trim();
  return who
    ? `${rowDate(snapshot.at_timestamp)} · pinned by ${who}`
    : rowDate(snapshot.at_timestamp);
}

/**
 * A synthetic `TaskMember` for the editorial "pinned by" avatar — `created_by`
 * is a short actor reference (often already initials, e.g. "AT"), so we feed
 * it through the theme-aware `<AvatarRow/>` purely for the disc + initials.
 * Display-only provenance, never a metric (§A9).
 */
function pinnerMember(who: string): TaskMember {
  return {
    membershipId: `pinner-${who}`,
    name: who,
    initials: deriveInitials(who),
    accountId: null,
  };
}

export interface SnapshotsViewProps {
  teamId: string | undefined;
  /** The team's roster, for assignee avatars in the opened as-of tree. */
  members: TaskMember[];
  /** Return to the live editor (the "← Back" affordance). */
  onExit: () => void;
}

export function SnapshotsView(props: SnapshotsViewProps) {
  const { teamId, members, onExit } = props;
  const { snapshots, isLoading, isError } = useTaskSnapshots(teamId);
  const createSnapshot = useCreateTaskSnapshot(teamId);

  const theme = useMapTheme();
  const c = theme.palette;
  const editorial = theme.mode === "editorial";

  const [openSnapshot, setOpenSnapshot] = useState<TaskSnapshot | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [label, setLabel] = useState("");
  // Optional past instant (a `<input type=date>` value, "YYYY-MM-DD"); empty = "now".
  const [atDate, setAtDate] = useState("");

  // Focus the label field when the pin form opens (ref, not `autoFocus`, so the
  // focus is intentional + lint-clean rather than an attribute side effect).
  const labelRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (formOpen) labelRef.current?.focus();
  }, [formOpen]);

  // An opened snapshot takes over the surface — render the read-only as-of view.
  if (openSnapshot) {
    return (
      <SnapshotAsOfView
        teamId={teamId}
        snapshot={openSnapshot}
        members={members}
        onBack={() => setOpenSnapshot(null)}
      />
    );
  }

  const submitPin = () => {
    const trimmed = label.trim();
    if (!trimmed) return;
    // A date-only value becomes end-of-day so "as of May 12" includes that day
    // (matching the rewind's day semantics); empty stays undefined → server "now".
    const atTimestamp = atDate ? new Date(`${atDate}T23:59:59.999`).toISOString() : undefined;
    createSnapshot.mutate(
      { label: trimmed, atTimestamp },
      {
        onSuccess: () => {
          setLabel("");
          setAtDate("");
          setFormOpen(false);
        },
      },
    );
  };

  return (
    <div data-testid="snapshots-view" className="flex h-[calc(100vh-8rem)] flex-col">
      {/* Header: back + title + "Pin current state". Editorial (`V_B34`) reads
          a mono `SNAPSHOTS` kicker + a serif-italic "Moments worth returning
          to." heading + a solid `.t-btn` pin; legacy keeps the `⟲ Snapshots`
          title + teal pin (byte-identical). The back affordance is preserved
          in both (behaviour unchanged). */}
      <div
        className="flex items-center justify-between gap-3 border-b px-4 py-2"
        style={{
          borderColor: editorial ? c.rule : PALETTE.softBorder,
          backgroundColor: editorial ? c.cardBg : "#FFFFFF",
        }}
      >
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="snapshots-back"
            onClick={onExit}
            className="rounded-md border px-2.5 py-1 text-xs font-semibold transition-colors hover:bg-slate-50"
            style={{
              borderColor: editorial ? c.rule2 : PALETTE.softBorder,
              color: editorial ? c.ink : PALETTE.slate,
            }}
          >
            ← Map
          </button>
          {editorial ? (
            <span className="flex flex-col leading-tight">
              <span
                data-testid="snapshots-kicker"
                className="text-[10px] uppercase"
                style={{ fontFamily: theme.fonts.mono, letterSpacing: "0.08em", color: c.muted }}
              >
                SNAPSHOTS
              </span>
              <span
                data-testid="snapshots-heading"
                className="text-lg italic"
                style={{ fontFamily: theme.fonts.serif, color: c.ink }}
              >
                Moments worth returning to.
              </span>
            </span>
          ) : (
            <span
              className="flex items-center gap-1.5 text-[13px] font-bold"
              style={{ color: PALETTE.ink }}
            >
              <span aria-hidden style={{ color: PALETTE.teal }}>
                ⟲
              </span>
              Snapshots
            </span>
          )}
        </div>
        <button
          type="button"
          data-testid="snapshots-pin"
          onClick={() => setFormOpen((v) => !v)}
          className={
            editorial
              ? "rounded-md border px-3 py-1.5 text-xs font-medium transition-opacity hover:opacity-90"
              : "rounded-md px-3 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90"
          }
          style={
            editorial
              ? { backgroundColor: c.ink, borderColor: c.ink, color: c.canvas }
              : { backgroundColor: PALETTE.teal }
          }
        >
          + Pin current state
        </button>
      </div>

      {/* The inline pin form (label + optional date). */}
      {formOpen && (
        <div
          data-testid="snapshots-pin-form"
          className="flex flex-wrap items-end gap-3 border-b px-4 py-3"
          style={{
            borderColor: editorial ? c.rule : PALETTE.softBorder,
            backgroundColor: editorial ? c.bgRecess : "#F7FBF9",
          }}
        >
          <label
            className="flex flex-col gap-1 text-[11px]"
            style={{ color: editorial ? c.muted : PALETTE.muted }}
          >
            Label
            <input
              ref={labelRef}
              data-testid="snapshots-label-input"
              type="text"
              value={label}
              placeholder="e.g. v1 submitted to ethics"
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitPin();
              }}
              className="w-72 rounded-md border px-2.5 py-1.5 text-[13px]"
              style={{
                borderColor: editorial ? c.rule2 : PALETTE.softBorder,
                color: editorial ? c.ink : PALETTE.ink,
                backgroundColor: editorial ? c.cardBg : undefined,
              }}
            />
          </label>
          <label
            className="flex flex-col gap-1 text-[11px]"
            style={{ color: editorial ? c.muted : PALETTE.muted }}
          >
            Moment (optional — defaults to now)
            <input
              data-testid="snapshots-date-input"
              type="date"
              value={atDate}
              onChange={(e) => setAtDate(e.target.value)}
              className="rounded-md border px-2.5 py-1.5 text-[13px]"
              style={{
                borderColor: editorial ? c.rule2 : PALETTE.softBorder,
                color: editorial ? c.ink : PALETTE.ink,
                backgroundColor: editorial ? c.cardBg : undefined,
              }}
            />
          </label>
          <button
            type="button"
            data-testid="snapshots-pin-submit"
            onClick={submitPin}
            disabled={!label.trim() || createSnapshot.isPending}
            className={
              editorial
                ? "rounded-md border px-3 py-1.5 text-xs font-medium transition-opacity hover:opacity-90 disabled:opacity-40"
                : "rounded-md px-3 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-40"
            }
            style={
              editorial
                ? { backgroundColor: c.ink, borderColor: c.ink, color: c.canvas }
                : { backgroundColor: PALETTE.teal }
            }
          >
            {createSnapshot.isPending ? "Pinning…" : "Pin"}
          </button>
        </div>
      )}

      {/* The list. */}
      <div
        className="min-h-0 flex-1 overflow-y-auto px-4 py-4"
        style={{ backgroundColor: editorial ? c.canvas : PALETTE.canvas }}
      >
        {isError ? (
          <div
            data-testid="snapshots-unavailable"
            className="flex h-full flex-col items-center justify-center gap-1 text-center"
            style={{ color: editorial ? c.muted : PALETTE.muted }}
          >
            <div className="text-sm font-semibold">Snapshots aren’t available yet</div>
            <div className="text-xs" style={{ color: editorial ? c.faint : PALETTE.faint }}>
              They’ll be ready here soon.
            </div>
          </div>
        ) : isLoading ? (
          <div
            data-testid="snapshots-loading"
            className="flex h-full items-center justify-center text-sm"
            style={{ color: editorial ? c.muted : PALETTE.muted }}
          >
            Loading snapshots…
          </div>
        ) : snapshots.length === 0 ? (
          <div
            data-testid="snapshots-empty"
            className="flex h-full flex-col items-center justify-center gap-1 text-center"
            style={{ color: editorial ? c.muted : PALETTE.muted }}
          >
            <div className="text-sm font-semibold">No snapshots yet</div>
            <div className="text-xs" style={{ color: editorial ? c.faint : PALETTE.faint }}>
              Pin a meaningful moment — “v1 submitted”, “post-review plan” — to return to it by
              name.
            </div>
          </div>
        ) : (
          <ul
            data-testid="snapshots-list"
            className={
              editorial
                ? "mx-auto w-full max-w-2xl overflow-hidden rounded-lg border"
                : "flex flex-col gap-3"
            }
            style={editorial ? { borderColor: c.rule, backgroundColor: c.cardBg } : undefined}
          >
            {snapshots.map((snapshot, i) => {
              const who = snapshot.created_by?.trim();
              return (
                <li key={snapshot.id}>
                  {editorial ? (
                    // `V_B34` row: a glyph + serif-italic name + a mono date ·
                    // "pinned by <Avatar> <name>" sub-line + a `.t-btn.link`
                    // open. Rows share one card with hairline dividers. The
                    // explicit "open →" button is the accessible action (no
                    // row-level click handler — keyboard-reachable + lint-clean).
                    <div
                      data-testid="snapshot-row"
                      className="flex items-center gap-4 px-5 py-4"
                      style={{
                        borderTop: i === 0 ? undefined : `1px solid ${c.rule}`,
                      }}
                    >
                      <span
                        aria-hidden
                        className="w-5 shrink-0 text-center text-lg"
                        style={{ fontFamily: theme.fonts.mono, color: c.slate }}
                      >
                        ◷
                      </span>
                      <div className="min-w-0 flex-1">
                        <div
                          className="truncate text-base italic"
                          style={{ fontFamily: theme.fonts.serif, color: c.ink }}
                        >
                          {snapshot.label}
                        </div>
                        <div
                          className="mt-0.5 flex items-center gap-1.5 text-[11.5px]"
                          style={{ color: c.muted }}
                        >
                          <span
                            className="uppercase"
                            style={{
                              fontFamily: theme.fonts.mono,
                              fontSize: "10.5px",
                              letterSpacing: "0.06em",
                            }}
                          >
                            {rowDate(snapshot.at_timestamp)}
                          </span>
                          {who && (
                            <>
                              <span>· pinned by</span>
                              <AvatarRow members={[pinnerMember(who)]} size={18} />
                              <span>{who}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        data-testid="snapshot-open"
                        onClick={() => setOpenSnapshot(snapshot)}
                        className="shrink-0 text-[12px] underline underline-offset-2"
                        style={{ color: c.teal }}
                      >
                        open →
                      </button>
                    </div>
                  ) : (
                    <div
                      data-testid="snapshot-row"
                      className="flex items-center gap-3 rounded-lg border bg-white px-4 py-3"
                      style={{ borderColor: PALETTE.softBorder }}
                    >
                      <span
                        aria-hidden
                        className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[13px]"
                        style={{ color: PALETTE.teal }}
                      >
                        ◷
                      </span>
                      <div className="min-w-0 flex-1">
                        <div
                          className="truncate text-[13px] font-semibold"
                          style={{ color: PALETTE.ink }}
                        >
                          {snapshot.label}
                        </div>
                        <div className="text-[11px]" style={{ color: "#9CA3AF" }}>
                          {pinnedByLabel(snapshot)}
                        </div>
                      </div>
                      <button
                        type="button"
                        data-testid="snapshot-open"
                        onClick={() => setOpenSnapshot(snapshot)}
                        className="shrink-0 rounded-md border px-3 py-1.5 text-[11px] font-semibold transition-colors hover:bg-slate-50"
                        style={{ borderColor: PALETTE.softBorder, color: PALETTE.muted }}
                      >
                        open →
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Footer note — the gallery's exact reassurance. */}
      {editorial ? (
        <div
          className="border-t px-4 py-3 text-center text-xs italic"
          style={{ borderColor: c.rule, backgroundColor: c.cardBg, color: c.muted }}
        >
          Snapshots are the moments worth returning to, by name.{" "}
          <span style={{ color: c.ink }}>Pinning never edits the plan.</span>
        </div>
      ) : (
        <div
          className="border-t px-4 py-2 text-[11px] italic"
          style={{ borderColor: PALETTE.softBorder, backgroundColor: "#FFFFFF", color: "#9CA3AF" }}
        >
          Snapshots are the moments worth returning to, by name. Pinning never edits the plan.
        </div>
      )}
    </div>
  );
}
